import { useEffect, useState } from "react";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/api";
import { naira } from "@/lib/format";

export default function AdminSettings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [newProd, setNewProd] = useState({ name: "", key: "", price_per_litre: "", min_order_litres: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [p, s] = await Promise.all([api.get("/admin/products"), api.get("/admin/settings")]);
    setProducts(p.data);
    setSettings(s.data);
  };
  useEffect(() => { load(); }, []);

  const saveProduct = async (prod) => {
    try {
      await api.put(`/admin/products/${prod.id}`, {
        price_per_litre: Number(prod.price_per_litre), available: prod.available, min_order_litres: Number(prod.min_order_litres),
      });
      toast.success(`${prod.name} updated`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const addProduct = async () => {
    if (!newProd.name || !newProd.key) { toast.error("Name and key are required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/products", { ...newProd, price_per_litre: Number(newProd.price_per_litre || 0), min_order_litres: Number(newProd.min_order_litres || 0) });
      toast.success("Product added"); setNewProd({ name: "", key: "", price_per_litre: "", min_order_litres: "" }); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const saveSettings = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", {
        ...settings, delivery_fee_default: Number(settings.delivery_fee_default || 0), tax_percent_default: Number(settings.tax_percent_default || 0),
      });
      toast.success("Settings saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!settings) return <AdminLayout><div className="text-slate-500 py-20 text-center">Loading…</div></AdminLayout>;
  const setS = (k) => (e) => setSettings((s) => ({ ...s, [k]: e.target.value }));

  return (
    <AdminLayout>
      <h1 className="font-display text-3xl font-bold text-slate-900">Settings</h1>
      {!isAdmin && <p className="mt-2 rounded bg-amber-50 px-3 py-2 text-sm text-amber-800 inline-block">Only administrators can change pricing and settings.</p>}

      {/* Products */}
      <div className="mt-6 rounded-lg border border-[#E5E7EB] bg-white">
        <div className="px-5 py-3 border-b border-[#E5E7EB]"><h3 className="font-display font-semibold text-slate-900">Products & Prices</h3></div>
        <div className="p-5 space-y-4">
          {products.map((p, i) => (
            <div key={p.id} data-testid={`setting-product-${p.key}`} className="grid sm:grid-cols-[1fr_auto_auto_auto_auto] gap-3 items-end border-b border-[#E5E7EB] pb-4 last:border-0 last:pb-0">
              <div><Label>{p.name}</Label><p className="text-xs text-slate-400">key: {p.key}</p></div>
              <div><Label className="text-xs">Price / L (₦)</Label><Input type="number" value={p.price_per_litre} disabled={!isAdmin} className="mt-1 w-32"
                onChange={(e) => setProducts((ps) => ps.map((x, j) => j === i ? { ...x, price_per_litre: e.target.value } : x))} /></div>
              <div><Label className="text-xs">Min litres</Label><Input type="number" value={p.min_order_litres} disabled={!isAdmin} className="mt-1 w-28"
                onChange={(e) => setProducts((ps) => ps.map((x, j) => j === i ? { ...x, min_order_litres: e.target.value } : x))} /></div>
              <div className="flex flex-col items-center"><Label className="text-xs">Available</Label>
                <Switch checked={p.available} disabled={!isAdmin} className="mt-2"
                  onCheckedChange={(v) => setProducts((ps) => ps.map((x, j) => j === i ? { ...x, available: v } : x))} /></div>
              <Button size="sm" onClick={() => saveProduct(p)} disabled={!isAdmin} className="bg-[#0A4F35] hover:bg-[#083D29] text-white" data-testid={`save-product-${p.key}`}>Save</Button>
            </div>
          ))}
          {isAdmin && (
            <div className="grid sm:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-3 items-end pt-4 border-t border-dashed border-[#E5E7EB]">
              <div><Label className="text-xs">New product name</Label><Input value={newProd.name} onChange={(e) => setNewProd((s) => ({ ...s, name: e.target.value }))} className="mt-1" placeholder="Kerosene (DPK)" data-testid="newprod-name" /></div>
              <div><Label className="text-xs">Key</Label><Input value={newProd.key} onChange={(e) => setNewProd((s) => ({ ...s, key: e.target.value.toLowerCase() }))} className="mt-1" placeholder="kerosene" data-testid="newprod-key" /></div>
              <div><Label className="text-xs">Price / L</Label><Input type="number" value={newProd.price_per_litre} onChange={(e) => setNewProd((s) => ({ ...s, price_per_litre: e.target.value }))} className="mt-1" /></div>
              <div><Label className="text-xs">Min litres</Label><Input type="number" value={newProd.min_order_litres} onChange={(e) => setNewProd((s) => ({ ...s, min_order_litres: e.target.value }))} className="mt-1" /></div>
              <Button size="sm" onClick={addProduct} disabled={busy} className="bg-[#E5B800] hover:bg-[#CCA300] text-black" data-testid="add-product-btn">Add</Button>
            </div>
          )}
        </div>
      </div>

      {/* Business settings */}
      <div className="mt-6 rounded-lg border border-[#E5E7EB] bg-white">
        <div className="px-5 py-3 border-b border-[#E5E7EB]"><h3 className="font-display font-semibold text-slate-900">Business Details</h3></div>
        <div className="p-5 grid sm:grid-cols-2 gap-4">
          <div><Label>Company name</Label><Input value={settings.company_name || ""} onChange={setS("company_name")} disabled={!isAdmin} className="mt-1" /></div>
          <div><Label>Phone</Label><Input value={settings.phone || ""} onChange={setS("phone")} disabled={!isAdmin} className="mt-1" /></div>
          <div><Label>WhatsApp number (intl, no +)</Label><Input value={settings.whatsapp_number || ""} onChange={setS("whatsapp_number")} disabled={!isAdmin} className="mt-1" placeholder="2348012345678" /></div>
          <div><Label>Email</Label><Input value={settings.email || ""} onChange={setS("email")} disabled={!isAdmin} className="mt-1" /></div>
          <div><Label>Address</Label><Input value={settings.address || ""} onChange={setS("address")} disabled={!isAdmin} className="mt-1" /></div>
          <div><Label>Default delivery fee (₦)</Label><Input type="number" value={settings.delivery_fee_default ?? 0} onChange={setS("delivery_fee_default")} disabled={!isAdmin} className="mt-1" /></div>
          <div className="sm:col-span-2"><Label>Indicative price note</Label><Textarea value={settings.indicative_note || ""} onChange={setS("indicative_note")} disabled={!isAdmin} className="mt-1" /></div>
          <div><Button onClick={saveSettings} disabled={busy || !isAdmin} className="bg-[#0A4F35] hover:bg-[#083D29] text-white font-semibold" data-testid="save-settings-btn">Save Settings</Button></div>
        </div>
      </div>
    </AdminLayout>
  );
}
